import { html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';
import type { Slice } from '../../types.js';

@customElement('slice-card')
export class SliceCard extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
                background: var(--surface);
                border: 2px solid var(--border);
                border-radius: 0.75rem;
                padding: 1.25rem;
                cursor: pointer;
                transition: all 0.2s;
                min-height: 160px;
                position: relative;
            }

            :host(:hover) {
                border-color: var(--primary-color);
                transform: translateY(-4px);
                box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
            }

            :host(:active) {
                transform: translateY(-2px);
            }

            :host([loading]) {
                opacity: 0.6;
                pointer-events: none;
                cursor: wait;
            }

            :host([loading])::after {
                content: "Loading...";
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 0.875rem;
                color: var(--text-secondary);
                font-weight: 600;
            }

            .card-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                gap: 0.5rem;
            }

            .card-title {
                flex: 1;
                font-size: 1.125rem;
                font-weight: 700;
                color: var(--text-primary);
                line-height: 1.3;
            }

            .card-artist {
                font-size: 0.875rem;
                color: var(--text-secondary);
                margin-bottom: 0.5rem;
            }

            .card-difficulty {
                display: inline-block;
                padding: 0.25rem 0.625rem;
                background-color: var(--primary-color);
                color: white;
                border-radius: 0.25rem;
                font-size: 0.75rem;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.025em;
                margin-bottom: 0.5rem;
                align-self: flex-start;
            }

            .card-tags {
                display: flex;
                flex-wrap: wrap;
                gap: 0.375rem;
                margin-top: auto;
            }

            .tag-chip {
                padding: 0.1875rem 0.5rem;
                background-color: var(--surface-light);
                color: var(--text-secondary);
                border-radius: 0.25rem;
                font-size: 0.6875rem;
                font-weight: 500;
            }

            .musicxml-status {
                flex-shrink: 0;
                width: 28px;
                height: 28px;
                border: 1.5px solid var(--border);
                border-radius: 6px;
                background: var(--surface-light);
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
            }

            .musicxml-status.has-musicxml {
                border-color: #34c759;
                background: #34c759;
                color: white;
            }

            .status-icon {
                font-size: 14px;
                line-height: 1;
            }

            .soundslice-badge {
                display: inline-block;
                padding: 0.125rem 0.5rem;
                background: #1a73e8;
                color: white;
                border-radius: 0.25rem;
                font-size: 0.625rem;
                font-weight: 600;
                letter-spacing: 0.03em;
                text-transform: uppercase;
                align-self: flex-start;
            }

            .admin-card-actions {
                display: flex;
                gap: 0.5rem;
                margin-top: 0.75rem;
                padding-top: 0.75rem;
                border-top: 1px solid var(--border);
            }

            .btn-admin {
                flex: 1;
                padding: 0.5rem;
                font-size: 0.8rem;
                border-radius: 4px;
                cursor: pointer;
                transition: background 0.2s, border-color 0.2s;
            }

            .btn-edit {
                background: var(--surface-light);
                border: 1px solid var(--border);
                color: var(--text-primary);
            }

            .btn-edit:hover {
                background: var(--primary-color);
                border-color: var(--primary-color);
            }

            .btn-delete {
                background: transparent;
                border: 1px solid var(--error-color);
                color: var(--error-color);
            }

            .btn-delete:hover {
                background: var(--error-color);
                color: white;
            }
        `,
    ];

    @property({ attribute: false })
    slice!: Slice;

    @property({ type: Boolean, reflect: true })
    admin = false;

    @property({ type: Boolean, reflect: true })
    loading = false;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('click', this._onHostClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('click', this._onHostClick);
    }

    private _onHostClick = (e: Event) => {
        // Check the composed path for clicks on excluded elements inside shadow DOM
        const path = e.composedPath() as HTMLElement[];
        const clickedExcluded = path.some(
            (el) => el instanceof HTMLElement && (
                el.classList?.contains('musicxml-status') ||
                el.classList?.contains('admin-card-actions')
            )
        );
        if (clickedExcluded) return;
        if (this.loading) return;

        this.dispatchEvent(
            new CustomEvent('slice-select', {
                bubbles: true,
                composed: true,
                detail: { slice: this.slice },
            })
        );
    };

    private _onEditClick(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.dispatchEvent(
            new CustomEvent('slice-edit', {
                bubbles: true,
                composed: true,
                detail: { slice: this.slice },
            })
        );
    }

    private _onDeleteClick(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.dispatchEvent(
            new CustomEvent('slice-delete', {
                bubbles: true,
                composed: true,
                detail: { slice: this.slice },
            })
        );
    }

    render() {
        if (!this.slice) return nothing;

        const hasMusicXML = this.slice.metadata?.musicxml;
        const uploadStatusClass = hasMusicXML ? 'has-musicxml' : '';
        const uploadTitle = hasMusicXML
            ? `MusicXML: ${this.slice.metadata!.musicxml!.originalName}`
            : 'Upload MusicXML';

        return html`
            <div class="card-header">
                <div class="card-title">${this.slice.name}</div>
                <span
                    class="musicxml-status ${uploadStatusClass}"
                    title=${uploadTitle}
                >
                    <span class="status-icon">${hasMusicXML ? '\u2713' : '\u2191'}</span>
                </span>
            </div>
            <div class="card-artist">${this.slice.artist || ''}</div>
            ${this.slice.soundsliceScorehash
                ? html`<span class="soundslice-badge">Soundslice</span>`
                : nothing}
            ${this.slice.difficulty
                ? html`<div class="card-difficulty">${this.slice.difficulty}</div>`
                : nothing}
            ${this.slice.tags && this.slice.tags.length > 0
                ? html`
                      <div class="card-tags">
                          ${this.slice.tags.map(
                              (tag) => html`<span class="tag-chip">#${tag}</span>`
                          )}
                      </div>
                  `
                : nothing}
            ${this.admin
                ? html`
                      <div class="admin-card-actions">
                          <button class="btn-admin btn-edit" title="Edit Slice" @click=${this._onEditClick}>
                              Edit
                          </button>
                          <button class="btn-admin btn-delete" title="Delete Slice" @click=${this._onDeleteClick}>
                              Delete
                          </button>
                      </div>
                  `
                : nothing}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'slice-card': SliceCard;
    }
}
